/**
 * 案件起票モーダル Step2（プロジェクト憲章、任意）フォーム。
 * ProjectIntakeModal から分離した表示専用コンポーネント。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件起票のプロジェクト憲章入力フォーム（目的/背景/目標など 10 項目の任意入力）
 * @graph-connects none
 */

import type { ReactNode } from 'react';
import { CharterField } from './ProjectIntakeControls.js';
import type { FormState, SetFormField } from './form-state.js';
import { isLightTaskKind } from '../../../types/project-intake.js';

/** @graph-connects none */
export interface ProjectIntakeStep2FormProps {
  /** フォーム状態 */
  form: FormState;
  /** フォーム 1 項目のセッター */
  set: SetFormField;
}

/** 憲章 1 項目の入力上限。サーバー側 schemas.ts の CHARTER_FIELD_MAX と揃える。 @graph-connects none */
const CHARTER_FIELD_MAX = 2000;

/** 憲章の可変入力項目に対応する FormState キー（いずれも string 値）。 @graph-connects none */
type CharterKey =
  | 'purpose'
  | 'background'
  | 'goal'
  | 'deliverables'
  | 'preconditions'
  | 'constraints'
  | 'schedule'
  | 'cost'
  | 'structure'
  | 'notes';

/** 憲章の可変項目（id / ラベル / FormState キー / 強調）定義。 @graph-connects none */
const CHARTER_FIELDS: {
  id: string;
  label: string;
  key: CharterKey;
  emphasis?: boolean;
}[] = [
  { id: 'c-purpose', label: '目的', key: 'purpose', emphasis: true },
  { id: 'c-background', label: '背景', key: 'background', emphasis: true },
  { id: 'c-goal', label: '目標', key: 'goal' },
  { id: 'c-deliverables', label: '成果物', key: 'deliverables' },
  { id: 'c-preconditions', label: '前提条件', key: 'preconditions' },
  { id: 'c-constraints', label: '制約条件', key: 'constraints' },
  { id: 'c-schedule', label: '概算スケジュール', key: 'schedule' },
  { id: 'c-cost', label: '概算コスト', key: 'cost' },
  { id: 'c-structure', label: '概算体制', key: 'structure' },
  { id: 'c-notes', label: '備考', key: 'notes' },
];

/**
 * Step2（プロジェクト憲章）の入力グリッド。PJ名は Step1 の値を read-only で再掲する。
 * @graph-connects none
 */
export function ProjectIntakeStep2Form({ form, set }: ProjectIntakeStep2FormProps): ReactNode {
  // 軽タスクでは「目的」の入力欄ラベルを「やりたいこと」と表示する。値の保存先は purpose のまま
  // （プロジェクト憲章の「目的」欄に回填される）で、UI 上の見せ方だけを切り替える。
  const lightTask = isLightTaskKind(form.kind);
  return (
    <div className="space-y-3">
      <CharterField id="c-pjname" label="PJ名" value={form.projectName} emphasis readOnly />
      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        {CHARTER_FIELDS.map((f) => (
          <CharterField
            key={f.id}
            id={f.id}
            label={lightTask && f.key === 'purpose' ? 'やりたいこと' : f.label}
            value={form[f.key]}
            emphasis={f.emphasis}
            maxLength={CHARTER_FIELD_MAX}
            onChange={(v) => {
              set(f.key, v);
            }}
          />
        ))}
      </div>
    </div>
  );
}
